import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../../context/AuthContext';
import { canSalesTarget, useSalesScope } from './salesTargetAccess';
import axiosInstance from '../../api/axiosConfig';
import CreateOrganizationModal from './modals/CreateOrganizationModal';
import EditOrganizationModal from './modals/EditOrganizationModal';
import SplitTargetModal from './modals/SplitTargetModal';
import SplitSalesmanTargetModal from './modals/SplitSalesmanTargetModal';
import TargetImportModal from './modals/TargetImportModal';
import SalesmanAvatar from './SalesmanAvatar';
import './OrganizationTree.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Icon helpers ─────────────────────────────────────────────
const IconRefresh = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);
const IconPlus = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const IconEdit = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const IconSplit = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
        <path d="M6 9v6" /><path d="M18 9A9 9 0 0 0 9 3" />
    </svg>
);
const IconUser = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
    </svg>
);
const IconBuilding = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 9h6M9 13h6M9 17h4" />
    </svg>
);
const IconBranch = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="18" cy="18" r="3" /><circle cx="6" cy="6" r="3" /><path d="M6 21V9a9 9 0 0 0 9 9" />
    </svg>
);
const IconChevronRight = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6" />
    </svg>
);
const IconUpload = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);
const IconMaximize = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
        <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
);
const IconClose = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IconTarget = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
);
const IconTrendUp = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
    </svg>
);
const IconGroups = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
);
const IconShop = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
);

// ── Constants ─────────────────────────────────────────────────
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const progressColor = (pct) => {
    if (pct >= 100) return '#10b981';
    if (pct >= 75) return '#3b82f6';
    if (pct >= 50) return '#f59e0b';
    return '#ef4444';
};

const statusLabel = (pct) => {
    if (pct >= 100) return { label: 'Excellent', cls: 'status-green' };
    if (pct >= 75) return { label: 'On Track', cls: 'status-blue' };
    if (pct >= 50) return { label: 'At Risk', cls: 'status-amber' };
    return { label: 'Behind', cls: 'status-red' };
};

const formatNum = (n) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n ?? 0);

const formatCurrency = (n) =>
    new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n ?? 0);

const getRelativeTime = (date) => {
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return past.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatDateTime = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

const getRankBadge = (index) => {
    if (index === 0) return <span className="rank-badge gold">1st</span>;
    if (index === 1) return <span className="rank-badge silver">2nd</span>;
    if (index === 2) return <span className="rank-badge bronze">3rd</span>;
    return <span className="rank-badge">{index + 1}th</span>;
};

// ── Circular Progress ─────────────────────────────────────────
const CircularProgress = ({ pct, size = 64, strokeWidth = 6 }) => {
    const radius = (size - strokeWidth) / 2;
    const circ = 2 * Math.PI * radius;
    const clamped = Math.min(pct, 100);
    const offset = circ - (clamped / 100) * circ;
    const color = progressColor(pct);
    return (
        <svg width={size} height={size} className="circ-progress-svg">
            <circle cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke="var(--bg-tertiary)" strokeWidth={strokeWidth} />
            <circle cx={size / 2} cy={size / 2} r={radius}
                fill="none" stroke={color} strokeWidth={strokeWidth}
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
            />
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                fontSize={size < 50 ? 8 : 11} fontWeight="700" fill={color}>
                {pct.toFixed(0)}%
            </text>
        </svg>
    );
};

// ── KPI Card ─────────────────────────────────────────────────
const KpiCard = ({ icon, label, value, sub, accent }) => (
    <div className={`kpi-card kpi-accent-${accent}`}>
        <div className="kpi-icon-wrap">{icon}</div>
        <div className="kpi-body">
            <div className="kpi-label">{label}</div>
            <div className="kpi-value">{value}</div>
            {sub && <div className="kpi-sub">{sub}</div>}
        </div>
    </div>
);

// ── Tree Node ─────────────────────────────────────────────────
const TreeNode = ({ node, onAddChild, onEdit, onSplit, selectedBranchId, onBranchClick, isAdmin }) => {
    const [expanded, setExpanded] = useState(true);
    const hasChildren = node.children && node.children.length > 0;
    const pct = node.completionPercentage || 0;
    const color = progressColor(pct);
    const clamp = Math.min(pct, 100);
    const status = statusLabel(pct);

    const typeIcon = {
        root: <IconBuilding />,
        company: <IconBuilding />,
        branch: <IconBranch />,
    }[node.type] || <IconBuilding />;

    const isBranch = node.type === 'branch';
    const isActive = isBranch && selectedBranchId === node._id;

    const handleCardClick = (e) => {
        if (e.target.closest('.node-action-btn') || e.target.closest('.tree-toggle-btn')) return;
        if (isBranch && onBranchClick) onBranchClick(node);
    };

    return (
        <li className={`tree-item ${expanded ? 'expanded' : 'collapsed'}`}>
            <div className="tree-node-wrapper">
                {/* Card */}
                <div
                    className={`tree-node-card type-${node.type} ${isActive ? 'active' : ''} ${isBranch ? 'clickable' : ''}`}
                    onClick={handleCardClick}
                >
                    {/* Status dot */}
                    <span className={`node-status-dot ${status.cls}`} title={status.label} />

                    {/* Header */}
                    <div className="node-card-header">
                        <div className="node-card-info">
                            <span className={`node-type-pill ${node.type}`}>
                                {typeIcon}
                                {node.type}
                            </span>
                            <span className="node-card-name" title={node.name}>
                                {node.name}
                                {isBranch && (
                                    <span className="clickable-indicator" title="Click to view reports">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </span>
                                )}
                            </span>
                            {node.managerNames && node.managerNames.length > 0 && (
                                <span className="node-manager-badge" title={node.managerNames.join(', ')}>
                                    <IconUser />
                                    {node.managerNames.join(', ')}
                                </span>
                            )}
                        </div>

                        {/* Circular progress ring */}
                        <CircularProgress pct={pct} size={node.type === 'root' ? 72 : node.type === 'company' ? 64 : 56} strokeWidth={6} />
                    </div>

                    {/* Metrics */}
                    <div className="node-card-metrics">
                        <div className="node-metric">
                            <span className="node-metric-label">Target</span>
                            <span className="node-metric-value">{formatNum(node.target)}</span>
                        </div>
                        <div className="node-metric">
                            <span className="node-metric-label">Achieved</span>
                            <span className="node-metric-value achieved">{formatNum(node.achievedSales)}</span>
                        </div>
                        <div className="node-metric">
                            <span className="node-metric-label">Status</span>
                            <span className={`node-status-badge ${status.cls}`}>{status.label}</span>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="node-progress-track">
                        <div
                            className="node-progress-fill"
                            style={{ width: `${clamp}%`, background: color }}
                        />
                    </div>

                    {/* Action buttons — visible on hover (admin only) */}
                    {isAdmin && (
                        <div className="node-card-actions">
                            <button className="node-action-btn add" title="Add child" onClick={() => onAddChild(node)}>
                                <IconPlus />
                            </button>
                            <button className="node-action-btn edit" title="Edit" onClick={() => onEdit(node)}>
                                <IconEdit />
                            </button>
                            {hasChildren && (
                                <button className="node-action-btn split" title="Split target" onClick={() => onSplit(node)}>
                                    <IconSplit />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Expand/Collapse toggle */}
                {hasChildren && (
                    <button
                        className={`tree-toggle-btn ${expanded ? 'expanded' : ''}`}
                        onClick={() => setExpanded(p => !p)}
                        title={expanded ? 'Collapse' : 'Expand'}
                    >
                        <IconChevronRight />
                    </button>
                )}
            </div>

            {/* Children */}
            {hasChildren && expanded && (
                <ul className="tree-children-list">
                    {node.children.map(child => (
                        <TreeNode
                            key={child._id}
                            node={child}
                            onAddChild={onAddChild}
                            onEdit={onEdit}
                            onSplit={onSplit}
                            selectedBranchId={selectedBranchId}
                            onBranchClick={onBranchClick}
                            isAdmin={isAdmin}
                        />
                    ))}
                </ul>
            )}
        </li>
    );
};

// ── Branch Report Drawer ──────────────────────────────────────
const BranchReportDrawer = ({ branch, period, year, month, quarter, onClose }) => {
    const { user } = useAuth();
    // Splitting is a POST (route: 'create') that the controller also gates on
    // 'edit' — a user needs both, or the server refuses it.
    const canSplit = canSalesTarget(user, 'create') && canSalesTarget(user, 'edit');
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isClosing, setIsClosing] = useState(false);
    const [showSplitTargetModal, setShowSplitTargetModal] = useState(false);

    useEffect(() => {
        if (!branch) return;
        fetchReports();
    }, [branch, period, year, month, quarter]);

    const fetchReports = async () => {
        try {
            setLoading(true); setError(null);
            const params = { branchId: branch._id, year };
            if (period === 'monthly') params.month = month;
            if (period === 'quarterly') params.quarter = quarter;

            // Use the new summary endpoint for aggregated data
            const summaryRes = await axiosInstance.get('/api/sales-target/reports/summary', { params });

            if (summaryRes.data.success) {
                const summaryData = summaryRes.data.data || [];
                const merged = summaryData.map(s => ({
                    _id: s.salesmanId,
                    salesmanId: s.salesmanId,
                    salesmanName: s.salesmanName,
                    employeeId: s.employeeId,
                    photo: s.photo,
                    totalSales: s.totalSales,
                    cashInvoice: s.cashInvoice,
                    creditInvoice: s.creditInvoice,
                    posSales: s.posSales,
                    reportCount: s.reportCount,
                    salesmanTarget: s.target || 0,
                    achievementPct: (s.target > 0) ? (s.totalSales / s.target) * 100 : 0
                })).sort((a, b) => b.achievementPct - a.achievementPct);
                setReports(merged);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => { onClose(); setIsClosing(false); }, 250);
    };

    if (!branch) return null;

    const periodLabel = period === 'monthly' ? `${MONTHS[month - 1]} ${year}` :
        period === 'quarterly' ? `Q${quarter} ${year}` : year;
    const totalSales = reports.reduce((sum, r) => sum + r.totalSales, 0);

    return (
        <div className="branch-drawer-overlay" onClick={handleClose}>
            <div className={`branch-drawer-panel ${isClosing ? 'closing' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="drawer-header">
                    <div className="drawer-title">
                        <span className="drawer-icon"><IconBranch /></span>
                        <div>
                            <h3>{branch.name}</h3>
                            <span className="drawer-period-badge">{periodLabel}</span>
                        </div>
                    </div>
                    <button className="drawer-close-btn" onClick={handleClose} title="Close (Esc)">
                        <IconClose />
                    </button>
                </div>

                <div className="drawer-summary">
                    <div className="ds-stat">
                        <span className="ds-label">Salesmen</span>
                        <span className="ds-value">{reports.length}</span>
                    </div>
                    <div className="ds-stat">
                        <span className="ds-label">Total Sales</span>
                        <span className="ds-value highlight">{formatNum(totalSales)}</span>
                    </div>
                    <div className="ds-stat">
                        <span className="ds-label">Branch Target</span>
                        <span className="ds-value">{formatNum(branch.target)}</span>
                    </div>
                    {canSplit && (
                        <div className="ds-stat">
                            <button className="btn-primary btn-sm" onClick={() => setShowSplitTargetModal(true)}>
                                🎯 Split Target
                            </button>
                        </div>
                    )}
                </div>

                <div className="drawer-content">
                    {loading ? (
                        <div className="drawer-loading"><div className="spinner" /></div>
                    ) : error ? (
                        <div className="drawer-error">
                            <p>⚠ {error}</p>
                            <button onClick={fetchReports} className="btn-secondary btn-sm">Retry</button>
                        </div>
                    ) : reports.length === 0 ? (
                        <div className="drawer-empty">
                            <p>No salesmen reports found for {periodLabel}.</p>
                        </div>
                    ) : (
                        <div className="drawer-table-wrapper">
                            <table className="drawer-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Rank</th>
                                        <th>Salesman</th>
                                        <th className="align-right">Total Sales</th>
                                        <th className="align-right">Target</th>
                                        <th className="align-right">Remaining</th>
                                        <th className="align-right">Achievement %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((r, idx) => {
                                        const salesmanTarget = r.salesmanTarget || 0;
                                        const achievementPct = salesmanTarget > 0 ? (r.totalSales / salesmanTarget) * 100 : 0;
                                        const remaining = salesmanTarget - r.totalSales;
                                        const color = progressColor(achievementPct);
                                        const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                        
                                        return (
                                            <tr key={r._id}>
                                                <td>{getRankBadge(idx)}</td>
                                                <td>
                                                    <div className="dt-salesman">
                                                        <SalesmanAvatar 
                                                            photo={r.photo}
                                                            name={r.salesmanName}
                                                            size={40}
                                                            rank={idx + 1}
                                                        />
                                                        <div className="dt-salesman-info">
                                                            <span className="dt-name">{r.salesmanName}</span>
                                                            <span className="dt-emp">{r.employeeId}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-right fw-bold">{formatNum(r.totalSales)}</td>
                                                <td className="align-right">{formatNum(salesmanTarget)}</td>
                                                <td className="align-right" style={{ color: remainingColor, fontWeight: 600 }}>
                                                    {remaining > 0 ? formatNum(remaining) : '✓ Done'}
                                                </td>
                                                <td className="align-right completion-pct" style={{ color }}>
                                                    {achievementPct.toFixed(1)}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <SplitSalesmanTargetModal
                    show={showSplitTargetModal}
                    branch={branch}
                    salesmen={reports}
                    month={month}
                    year={year}
                    onClose={() => setShowSplitTargetModal(false)}
                    onSuccess={() => { setShowSplitTargetModal(false); fetchReports(); }}
                />
            </div>
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────
const OrganizationTree = forwardRef((props, ref) => {
    const { user } = useAuth();
    
    // Check if user is admin
    const isAdmin = user && user.role === 'system_admin';
    
    // "Add Organization" creates a ROOT organization: a POST ('create'), and the
    // server refuses a new root from anyone limited to some branches.
    const scope = useSalesScope(user);
    const canAddRootOrganization = canSalesTarget(user, 'create') && !!scope?.unrestricted;
    // The template import POSTs ('create') and sets targets ('edit') — same pair as Split.
    const canImportTargets = canSalesTarget(user, 'create') && canSalesTarget(user, 'edit');
    const [treeData, setTreeData] = useState([]);
    const [dashStats, setDashStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [period, setPeriod] = useState('monthly');
    const [year, setYear] = useState(CURRENT_YEAR);
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [showSplit, setShowSplit] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [selected, setSelected] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [showZoomModal, setShowZoomModal] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [lastTouchDistance, setLastTouchDistance] = useState(null);

    // Cascading leaderboard states
    const [companyLeaderboard, setCompanyLeaderboard] = useState([]);
    const [branchLeaderboard, setBranchLeaderboard] = useState([]);
    const [salesmanLeaderboard, setSalesmanLeaderboard] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedLeaderboardBranch, setSelectedLeaderboardBranch] = useState(null);
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);

    // Latest upload timestamp state
    const [latestUpload, setLatestUpload] = useState(null);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') {
                if (showZoomModal) {
                    setShowZoomModal(false);
                    setZoomLevel(1);
                    setPanPosition({ x: 0, y: 0 });
                } else if (selectedBranch) {
                    setSelectedBranch(null);
                }
            }
            
            // Zoom controls when modal is open
            if (showZoomModal) {
                if (e.key === '+' || e.key === '=') {
                    e.preventDefault();
                    handleZoomIn();
                } else if (e.key === '-' || e.key === '_') {
                    e.preventDefault();
                    handleZoomOut();
                } else if (e.key === '0' || e.key === 'r' || e.key === 'R') {
                    e.preventDefault();
                    handleResetZoom();
                }
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [selectedBranch, showZoomModal]);

    useEffect(() => { fetchTree(); fetchDashStats(); }, [period, year, month, quarter]);

    // Add non-passive wheel event listener for zoom modal
    useEffect(() => {
        if (!showZoomModal) return;

        const modalContent = document.querySelector('.zoom-modal-content');
        if (!modalContent) return;

        const handleWheelEvent = (e) => {
            e.preventDefault();
            const delta = e.deltaY > 0 ? -0.1 : 0.1;
            setZoomLevel(prev => Math.max(0.5, Math.min(3, prev + delta)));
        };

        modalContent.addEventListener('wheel', handleWheelEvent, { passive: false });
        return () => modalContent.removeEventListener('wheel', handleWheelEvent);
    }, [showZoomModal]);

    // Update leaderboards when tree data changes
    useEffect(() => {
        if (treeData && treeData.length > 0) {
            fetchLeaderboards();
            fetchLatestUpload(); // Fetch latest upload after tree data is loaded
        }
    }, [treeData]);

    const fetchTree = async () => {
        try {
            setLoading(true); setError(null);
            const params = { period, year };
            if (period === 'monthly') params.month = month;
            if (period === 'quarterly') params.quarter = quarter;
            const res = await axiosInstance.get('/api/sales-target/performance/tree', { params });
            if (res.data.success) setTreeData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load organization tree');
        } finally {
            setLoading(false);
        }
    };

    const fetchDashStats = async () => {
        try {
            const res = await axiosInstance.get('/api/sales-target/dashboard/stats', {
                params: { year: CURRENT_YEAR, month: new Date().getMonth() + 1 }
            });
            if (res.data.success) setDashStats(res.data.data);
        } catch { /* silent */ }
    };

    const fetchLatestUpload = async () => {
        try {
            const params = { year };
            if (period === 'monthly') params.month = month;
            if (period === 'quarterly') params.quarter = quarter;
            
            // Get first company ID if available
            if (treeData && treeData.length > 0) {
                const firstNode = treeData[0];
                if (firstNode.type === 'company' || firstNode.type === 'root') {
                    params.companyId = firstNode._id;
                }
            }
            
            const res = await axiosInstance.get('/api/sales-target/reports/latest-upload', { params });
            if (res.data.success && res.data.data) {
                setLatestUpload(res.data.data);
            } else {
                setLatestUpload(null);
            }
        } catch { 
            setLatestUpload(null);
        }
    };

    const fetchLeaderboards = async () => {
        // Extract leaderboards from existing tree data
        if (!treeData || treeData.length === 0) {
            setCompanyLeaderboard([]);
            setBranchLeaderboard([]);
            setSalesmanLeaderboard([]);
            return;
        }

        try {
            setLeaderboardLoading(true);
            
            // Flatten tree to get all nodes
            const allNodes = [];
            const walkTree = (nodes) => {
                nodes.forEach(node => {
                    allNodes.push(node);
                    if (node.children && node.children.length > 0) {
                        walkTree(node.children);
                    }
                });
            };
            walkTree(treeData);

            // Extract companies
            let companies = allNodes
                .filter(n => n.type === 'company')
                .map(c => ({
                    _id: c._id,
                    name: c.name,
                    achievedSales: c.achievedSales || 0,
                    target: c.target || 0,
                    completionPercentage: c.completionPercentage || 0,
                    children: c.children || []
                }))
                .sort((a, b) => b.completionPercentage - a.completionPercentage);
            
            // If no companies found, treat root organizations as companies
            if (companies.length === 0) {
                companies = treeData
                    .filter(n => n.type === 'root')
                    .map(c => ({
                        _id: c._id,
                        name: c.name,
                        achievedSales: c.achievedSales || 0,
                        target: c.target || 0,
                        completionPercentage: c.completionPercentage || 0,
                        children: c.children || []
                    }))
                    .sort((a, b) => b.completionPercentage - a.completionPercentage);
            }
            
            // A scoped user can own a whole company AND a single branch of another
            // one; that branch sits at the top of their tree beside the company
            // card. Group such loose branches under one entry, or they could never
            // be reached from the branch/salesman leaderboards.
            const looseBranches = treeData.filter(n => n.type === 'branch');
            if (companies.length > 0 && looseBranches.length > 0) {
                const target = looseBranches.reduce((s, b) => s + (b.target || 0), 0);
                const achievedSales = looseBranches.reduce((s, b) => s + (b.achievedSales || 0), 0);
                companies = [...companies, {
                    _id: 'my-branches',
                    name: 'Other Branches',
                    achievedSales,
                    target,
                    completionPercentage: target > 0 ? Math.round((achievedSales / target) * 10000) / 100 : 0,
                    children: looseBranches
                }].sort((a, b) => b.completionPercentage - a.completionPercentage);
            }

            setCompanyLeaderboard(companies);

            // Auto-select first company
            if (companies.length > 0) {
                // Keep existing selection if still valid
                const existingSelection = companies.find(c => c._id === selectedCompany?._id);
                if (existingSelection) {
                    handleCompanySelect(existingSelection);
                } else {
                    handleCompanySelect(companies[0]);
                }
            } else {
                // No company card (a user who owns only branches): the branch
                // leaderboard lists their branches directly instead of waiting
                // for a company selection that can never happen.
                handleCompanySelect({ _id: 'my-branches', name: 'My Branches', children: treeData });
            }
        } catch (err) {
            console.error('Failed to process leaderboards:', err);
        } finally {
            setLeaderboardLoading(false);
        }
    };

    const handleCompanySelect = (company) => {
        setSelectedCompany(company);
        
        // Extract all branches from company's children (recursively)
        const extractBranches = (nodes) => {
            let branches = [];
            nodes.forEach(node => {
                if (node.type === 'branch') {
                    branches.push({
                        _id: node._id,
                        name: node.name,
                        achievedSales: node.achievedSales || 0,
                        target: node.target || 0,
                        completionPercentage: node.completionPercentage || 0
                    });
                }
                if (node.children && node.children.length > 0) {
                    branches = branches.concat(extractBranches(node.children));
                }
            });
            return branches;
        };
        
        const branches = extractBranches(company.children || [])
            .sort((a, b) => b.completionPercentage - a.completionPercentage);
        
        setBranchLeaderboard(branches);
        
        // Auto-select first branch or keep existing if valid
        if (branches.length > 0) {
            const existingSelection = branches.find(b => b._id === selectedLeaderboardBranch?._id);
            if (existingSelection) {
                handleBranchSelect(existingSelection);
            } else {
                handleBranchSelect(branches[0]);
            }
        } else {
            setSalesmanLeaderboard([]);
            setSelectedLeaderboardBranch(null);
        }
    };

    const handleBranchSelect = async (branch) => {
        setSelectedLeaderboardBranch(branch);
        
        try {
            const params = { branchId: branch._id, year };
            if (period === 'monthly') params.month = month;
            if (period === 'quarterly') params.quarter = quarter;
            
            // Use the new summary endpoint for aggregated data
            const summaryRes = await axiosInstance.get('/api/sales-target/reports/summary', { params });
            
            if (summaryRes.data.success) {
                const summaryData = summaryRes.data.data || [];
                
                const salesmen = summaryData.map(s => ({
                    _id: s.salesmanId,
                    salesmanId: s.salesmanId,
                    salesmanName: s.salesmanName,
                    employeeId: s.employeeId,
                    photo: s.photo,
                    totalSales: s.totalSales,
                    cashInvoice: s.cashInvoice,
                    creditInvoice: s.creditInvoice,
                    posSales: s.posSales,
                    reportCount: s.reportCount,
                    target: s.target || 0,
                    completionPercentage: s.target > 0 
                        ? (s.totalSales / s.target) * 100 
                        : 0
                })).sort((a, b) => b.completionPercentage - a.completionPercentage);
                
                setSalesmanLeaderboard(salesmen);
            }
        } catch (err) {
            console.error('Failed to fetch salesman data:', err);
            setSalesmanLeaderboard([]);
        }
    };

    useImperativeHandle(ref, () => ({ refreshTree: fetchTree }));

    const onAddChild = (node) => { setSelected(node); setShowCreate(true); };
    const onEdit = (node) => { setSelected(node); setShowEdit(true); };
    const onSplit = (node) => { setSelected(node); setShowSplit(true); };
    const onBranchClick = (node) => {
        setSelectedBranch(prev => (prev && prev._id === node._id) ? null : node);
    };

    const handleTreeContentClick = (e) => {
        // Don't open modal if clicking on buttons or interactive elements
        if (e.target.closest('.node-action-btn') || 
            e.target.closest('.tree-toggle-btn') || 
            e.target.closest('.tree-node-card.clickable')) {
            return;
        }
        setShowZoomModal(true);
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
    };

    const handleMouseDown = (e) => {
        if (e.target.closest('.tree-node-card') || 
            e.target.closest('.node-action-btn') || 
            e.target.closest('.tree-toggle-btn')) {
            return;
        }
        setIsDragging(true);
        setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        setPanPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // Touch event handlers for mobile support
    const getTouchDistance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
        if (e.target.closest('.tree-node-card') || 
            e.target.closest('.node-action-btn') || 
            e.target.closest('.tree-toggle-btn')) {
            return;
        }

        if (e.touches.length === 1) {
            // Single touch - start panning
            setIsDragging(true);
            setDragStart({ 
                x: e.touches[0].clientX - panPosition.x, 
                y: e.touches[0].clientY - panPosition.y 
            });
        } else if (e.touches.length === 2) {
            // Two fingers - prepare for pinch zoom
            e.preventDefault();
            setIsDragging(false);
            setLastTouchDistance(getTouchDistance(e.touches));
        }
    };

    const handleTouchMove = (e) => {
        if (e.touches.length === 1 && isDragging) {
            // Single touch - pan
            e.preventDefault();
            setPanPosition({
                x: e.touches[0].clientX - dragStart.x,
                y: e.touches[0].clientY - dragStart.y
            });
        } else if (e.touches.length === 2 && lastTouchDistance) {
            // Two fingers - pinch zoom
            e.preventDefault();
            const currentDistance = getTouchDistance(e.touches);
            const delta = (currentDistance - lastTouchDistance) * 0.01;
            setZoomLevel(prev => Math.max(0.5, Math.min(3, prev + delta)));
            setLastTouchDistance(currentDistance);
        }
    };

    const handleTouchEnd = (e) => {
        if (e.touches.length === 0) {
            setIsDragging(false);
            setLastTouchDistance(null);
        } else if (e.touches.length === 1) {
            // One finger left, reset to pan mode
            setLastTouchDistance(null);
            setIsDragging(true);
            setDragStart({ 
                x: e.touches[0].clientX - panPosition.x, 
                y: e.touches[0].clientY - panPosition.y 
            });
        }
    };

    // Flatten tree to compute totals
    const allNodes = [];
    const walk = (nodes) => nodes.forEach(n => { allNodes.push(n); if (n.children) walk(n.children); });
    walk(treeData);
    
    // Calculate total target: Use root organization target if exists, otherwise sum branch targets
    let totalTarget = 0;
    let totalAchieved = 0;
    
    const rootOrg = treeData.find(n => n.type === 'root');
    if (rootOrg && rootOrg.target > 0) {
        // Use root organization target as the period target
        totalTarget = rootOrg.target;
        totalAchieved = rootOrg.achievedSales || 0;
    } else if (!rootOrg) {
        // A branch-scoped user's tree starts at the cards they own (their
        // branches, or a whole company) — there is no root card. Those
        // top-level cards are exactly their total.
        totalTarget = treeData.reduce((s, n) => s + (n.target || 0), 0);
        totalAchieved = treeData.reduce((s, n) => s + (n.achievedSales || 0), 0);
    } else {
        // Fallback: sum all branch targets (only leaf branches to avoid double counting)
        const leafBranches = allNodes.filter(n => n.type === 'branch' && (!n.children || n.children.length === 0));
        totalTarget = leafBranches.reduce((s, n) => s + (n.target || 0), 0);
        totalAchieved = leafBranches.reduce((s, n) => s + (n.achievedSales || 0), 0);
    }
    const overallPct = totalTarget > 0 ? (totalAchieved / totalTarget) * 100 : 0;
    const hasCompanyLevel = allNodes.some(n => n.type === 'company' || n.type === 'root');

    const periodLabel = period === 'monthly' ? `${MONTHS[month - 1]} ${year}` :
        period === 'quarterly' ? `Q${quarter} ${year}` : `${year}`;

    if (loading) {
        return (
            <div className="org-dashboard">
                <div className="tree-loading">
                    <div className="spinner" />
                    <p>Loading dashboard…</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="org-dashboard">
                <div className="tree-error">
                    <p>⚠ {error}</p>
                    <button className="tree-retry-btn" onClick={fetchTree}><IconRefresh /> Retry</button>
                </div>
            </div>
        );
    }

    return (
        <div className={`org-dashboard${selectedBranch ? ' drawer-open' : ''}`}>

            {/* ── KPI Hero Row ── */}
            <div className="kpi-row">
                <KpiCard
                    accent="primary"
                    icon={<IconTarget />}
                    label="Period Target"
                    value={formatCurrency(totalTarget)}
                    sub={`${periodLabel} · ${allNodes.filter(n => n.type === 'branch').length} branches`}
                />
                <KpiCard
                    accent="success"
                    icon={<IconTrendUp />}
                    label="Period Achieved"
                    value={formatCurrency(totalAchieved)}
                    sub={`${overallPct.toFixed(1)}% completion`}
                />
                {dashStats && (
                    <>
                        <KpiCard
                            accent="info"
                            icon={<IconTrendUp />}
                            label="Monthly Sales (Current)"
                            value={formatCurrency(dashStats.monthly?.totalSales)}
                            sub={`${dashStats.monthly?.reportCount || 0} reports`}
                        />
                        <KpiCard
                            accent="warning"
                            icon={<IconShop />}
                            label="Branches"
                            value={dashStats.counts?.totalBranches || 0}
                            sub={`${dashStats.counts?.totalOrganizations || 0} total orgs`}
                        />
                        <KpiCard
                            accent="purple"
                            icon={<IconGroups />}
                            label="Salesmen"
                            value={dashStats.counts?.totalSalesmen || 0}
                            sub="Active"
                        />
                    </>
                )}
            </div>

            {/* ── Latest Upload Info ── */}
            {latestUpload && (
                <div className="latest-upload-banner">
                    <span className="latest-upload-icon">📊</span>
                    <span className="latest-upload-text">
                        Last report uploaded: <strong>{getRelativeTime(latestUpload.lastUploadDate)}</strong> on {formatDateTime(latestUpload.lastUploadDate)}
                    </span>
                </div>
            )}

            {/* ── Cascading Leaderboards Row ── */}
            {/* The company box only makes sense when the tree has a company or root
                card; a user who owns only branches gets the other two boxes. */}
            <div className={`cascading-leaderboards-row${hasCompanyLevel ? '' : ' no-company'}`}>
                {/* Company Leaderboard */}
                {hasCompanyLevel && (
                <div className="leaderboard-box">
                    <div className="leaderboard-box-header">
                        <IconBuilding />
                        <span>Company Leaderboard</span>
                    </div>
                    <div className="leaderboard-box-content">
                        {leaderboardLoading ? (
                            <div className="leaderboard-loading"><div className="spinner" /></div>
                        ) : companyLeaderboard.length === 0 ? (
                            <div className="leaderboard-empty">No companies found</div>
                        ) : (
                            <ul className="leaderboard-list">
                                {companyLeaderboard.map((company, idx) => (
                                    <li 
                                        key={company._id}
                                        className={`leaderboard-item ${selectedCompany?._id === company._id ? 'selected' : ''}`}
                                        onClick={() => handleCompanySelect(company)}
                                    >
                                        <span className="lb-rank">{getRankBadge(idx)}</span>
                                        <div className="lb-info">
                                            <span className="lb-name">{company.name}</span>
                                            <span className="lb-details">
                                                {formatCurrency(company.achievedSales)} / {formatCurrency(company.target)}
                                            </span>
                                            {(() => {
                                                const remaining = company.target - company.achievedSales;
                                                const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                                return (
                                                    <span className="lb-remaining" style={{ color: remainingColor }}>
                                                        {remaining > 0 ? `Remaining: ${formatCurrency(remaining)}` : '✓ Target Achieved'}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <div className="lb-progress-mini">
                                            <div 
                                                className="lb-progress-fill-mini" 
                                                style={{ 
                                                    width: `${Math.min(company.completionPercentage, 100)}%`,
                                                    background: progressColor(company.completionPercentage)
                                                }}
                                            />
                                            <span className="lb-percentage" style={{ color: progressColor(company.completionPercentage) }}>
                                                {company.completionPercentage.toFixed(1)}%
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                )}

                {/* Branch Leaderboard */}
                <div className="leaderboard-box">
                    <div className="leaderboard-box-header">
                        <IconBranch />
                        <span>Branch Leaderboard</span>
                        {selectedCompany && <span className="lb-subtitle">{selectedCompany.name}</span>}
                    </div>
                    <div className="leaderboard-box-content">
                        {!selectedCompany ? (
                            <div className="leaderboard-empty">Select a company</div>
                        ) : branchLeaderboard.length === 0 ? (
                            <div className="leaderboard-empty">No branches found</div>
                        ) : (
                            <ul className="leaderboard-list">
                                {branchLeaderboard.map((branch, idx) => (
                                    <li 
                                        key={branch._id}
                                        className={`leaderboard-item ${selectedLeaderboardBranch?._id === branch._id ? 'selected' : ''}`}
                                        onClick={() => handleBranchSelect(branch)}
                                    >
                                        <span className="lb-rank">{getRankBadge(idx)}</span>
                                        <div className="lb-info">
                                            <span className="lb-name">{branch.name}</span>
                                            <span className="lb-details">
                                                {formatCurrency(branch.achievedSales)} / {formatCurrency(branch.target)}
                                            </span>
                                            {(() => {
                                                const remaining = branch.target - branch.achievedSales;
                                                const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                                return (
                                                    <span className="lb-remaining" style={{ color: remainingColor }}>
                                                        {remaining > 0 ? `Remaining: ${formatCurrency(remaining)}` : '✓ Target Achieved'}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <div className="lb-progress-mini">
                                            <div 
                                                className="lb-progress-fill-mini" 
                                                style={{ 
                                                    width: `${Math.min(branch.completionPercentage, 100)}%`,
                                                    background: progressColor(branch.completionPercentage)
                                                }}
                                            />
                                            <span className="lb-percentage" style={{ color: progressColor(branch.completionPercentage) }}>
                                                {branch.completionPercentage.toFixed(1)}%
                                            </span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                {/* Salesman Leaderboard */}
                <div className="leaderboard-box">
                    <div className="leaderboard-box-header">
                        <IconUser />
                        <span>Salesman Leaderboard</span>
                        {selectedLeaderboardBranch && <span className="lb-subtitle">{selectedLeaderboardBranch.name}</span>}
                    </div>
                    <div className="leaderboard-box-content">
                        {!selectedLeaderboardBranch ? (
                            <div className="leaderboard-empty">Select a branch</div>
                        ) : salesmanLeaderboard.length === 0 ? (
                            <div className="leaderboard-empty">No salesmen found</div>
                        ) : (
                            <ul className="leaderboard-list">
                                {salesmanLeaderboard.map((salesman, idx) => {
                                    const remaining = salesman.target - salesman.totalSales;
                                    const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                    
                                    return (
                                        <li key={salesman._id} className="leaderboard-item">
                                            <span className="lb-rank">{getRankBadge(idx)}</span>
                                            <SalesmanAvatar 
                                                photo={salesman.photo}
                                                name={salesman.salesmanName}
                                                size={40}
                                                rank={idx + 1}
                                            />
                                            <div className="lb-info">
                                                <span className="lb-name">{salesman.salesmanName}</span>
                                                <span className="lb-emp-id">{salesman.employeeId}</span>
                                                <span className="lb-details">
                                                    {formatCurrency(salesman.totalSales)} / {formatCurrency(salesman.target)}
                                                </span>
                                                <span className="lb-remaining" style={{ color: remainingColor }}>
                                                    {remaining > 0 ? `Remaining: ${formatCurrency(remaining)}` : '✓ Target Achieved'}
                                                </span>
                                            </div>
                                            <div className="lb-progress-mini">
                                                <div 
                                                    className="lb-progress-fill-mini" 
                                                    style={{ 
                                                        width: `${Math.min(salesman.completionPercentage, 100)}%`,
                                                        background: progressColor(salesman.completionPercentage)
                                                    }}
                                                />
                                                <span className="lb-percentage" style={{ color: progressColor(salesman.completionPercentage) }}>
                                                    {salesman.completionPercentage.toFixed(1)}%
                                                </span>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Mini Leaderboards from dashboard/stats (no extra call) ── */}
            {dashStats && (dashStats.topBranches?.length > 0 || dashStats.topSalesmen?.length > 0) && (
                <div className="mini-lb-row">
                    {/* Top Branches */}
                    {dashStats.topBranches?.length > 0 && (
                        <div className="mini-lb-card">
                            <div className="mini-lb-header">
                                <IconShop />
                                <span>Top Branches — {MONTHS[new Date().getMonth()]} {CURRENT_YEAR}</span>
                            </div>
                            <ol className="mini-lb-list">
                                {dashStats.topBranches.map((b, i) => {
                                    const achievementPct = b.achievementPct || 0;
                                    const rankLabel = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i + 1}`;
                                    return (
                                        <li key={i} className={`mini-lb-item rank-${Math.min(i + 1, 5)}`}>
                                            <span className="mini-lb-rank">{rankLabel}</span>
                                            <div className="mini-lb-info-col">
                                                <span className="mini-lb-name">{b.branchName}</span>
                                                <span className="mini-lb-details">
                                                    {formatCurrency(b.achievedSales)} / {formatCurrency(b.target)}
                                                </span>
                                                {(() => {
                                                    const remaining = b.target - b.achievedSales;
                                                    const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                                    return (
                                                        <span className="mini-lb-remaining" style={{ color: remainingColor }}>
                                                            {remaining > 0 ? `Rem: ${formatCurrency(remaining)}` : '✓ Target Achieved'}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <span className="mini-lb-val" style={{ color: progressColor(achievementPct) }}>
                                                {achievementPct.toFixed(1)}%
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                    {/* Top Salesmen */}
                    {dashStats.topSalesmen?.length > 0 && (
                        <div className="mini-lb-card">
                            <div className="mini-lb-header">
                                <IconGroups />
                                <span>Top Salesmen — {MONTHS[new Date().getMonth()]} {CURRENT_YEAR}</span>
                            </div>
                            <ol className="mini-lb-list">
                                {dashStats.topSalesmen.map((s, i) => {
                                    const achievementPct = s.achievementPct || 0;
                                    return (
                                        <li key={i} className={`mini-lb-item rank-${i + 1}`}>
                                            <span className="mini-lb-rank">{['🥇', '🥈', '🥉', '4', '5'][i]}</span>
                                            <SalesmanAvatar 
                                                photo={s.photo}
                                                name={s.salesmanName}
                                                size={36}
                                                rank={i + 1}
                                            />
                                            <div className="mini-lb-name-block">
                                                <span className="mini-lb-name">{s.salesmanName}</span>
                                                <span className="mini-lb-sub">{s.branchName}</span>
                                                <span className="mini-lb-details">
                                                    {formatCurrency(s.achievedSales)} / {formatCurrency(s.target)}
                                                </span>
                                                {(() => {
                                                    const remaining = s.target - s.achievedSales;
                                                    const remainingColor = remaining > 0 ? '#f59e0b' : '#10b981';
                                                    return (
                                                        <span className="mini-lb-remaining" style={{ color: remainingColor }}>
                                                            {remaining > 0 ? `Rem: ${formatCurrency(remaining)}` : '✓ Target Achieved'}
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                            <span className="mini-lb-val" style={{ color: progressColor(achievementPct) }}>
                                                {achievementPct.toFixed(1)}%
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        </div>
                    )}
                </div>
            )}

            {/* ── Overall Completion Banner ── */}
            {treeData.length > 0 && (
                <div className="overall-banner">
                    <div className="banner-left">
                        <CircularProgress pct={overallPct} size={80} strokeWidth={8} />
                        <div>
                            <p className="banner-title">Overall Target Completion — {periodLabel}</p>
                            <p className="banner-sub">
                                {formatNum(totalAchieved)} / {formatNum(totalTarget)} achieved across all branches
                            </p>
                        </div>
                    </div>
                    <div className="banner-progress-track">
                        <div
                            className="banner-progress-fill"
                            style={{ width: `${Math.min(overallPct, 100)}%`, background: progressColor(overallPct) }}
                        />
                    </div>
                </div>
            )}

            {/* ── Filter & Controls Bar ── */}
            <div className="tree-filters">
                <div className="filter-bar-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                        <path d="M3 3h6v4H3zM15 3h6v4h-6zM9 17h6v4H9z" />
                        <line x1="6" y1="7" x2="6" y2="14" /><line x1="18" y1="7" x2="18" y2="12" />
                        <line x1="12" y1="21" x2="12" y2="12" /><line x1="6" y1="14" x2="18" y2="14" />
                        <line x1="12" y1="14" x2="12" y2="17" />
                    </svg>
                    Target Tracker
                </div>

                <div className="filter-group">
                    <label>Period</label>
                    <select className="filter-select" value={period} onChange={e => setPeriod(e.target.value)}>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="yearly">Yearly</option>
                    </select>
                </div>

                <div className="filter-group">
                    <label>Year</label>
                    <select className="filter-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
                        {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                </div>

                {period === 'monthly' && (
                    <div className="filter-group">
                        <label>Month</label>
                        <select className="filter-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
                            {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                        </select>
                    </div>
                )}

                {period === 'quarterly' && (
                    <div className="filter-group">
                        <label>Quarter</label>
                        <select className="filter-select" value={quarter} onChange={e => setQuarter(parseInt(e.target.value))}>
                            <option value={1}>Q1 — Jan–Mar</option>
                            <option value={2}>Q2 — Apr–Jun</option>
                            <option value={3}>Q3 — Jul–Sep</option>
                            <option value={4}>Q4 — Oct–Dec</option>
                        </select>
                    </div>
                )}

                <div className="tree-filter-actions">
                    <button className="tree-refresh-btn" onClick={fetchTree} title="Refresh">
                        <IconRefresh /> Refresh
                    </button>
                    {canAddRootOrganization&& (
                        <button className="tree-add-root-btn"
                            onClick={() => { setSelected(null); setShowCreate(true); }}
                            title="Add root organization">
                            <IconPlus /> Add Organization
                        </button>
                    )}
                    {canImportTargets && (
                        <button className="tree-import-btn"
                            onClick={() => setShowImport(true)}
                            title="Set this month's targets from an Excel template">
                            <IconUpload /> Import Targets
                        </button>
                    )}
                    <button className="tree-fullscreen-btn"
                        onClick={() => setShowZoomModal(true)}
                        title="Open zoom view">
                        <IconMaximize />
                        Zoom View
                    </button>
                </div>
            </div>

            {/* ── Tree Content ── */}
            <div className="tree-content" onClick={handleTreeContentClick}>
                {treeData.length === 0 ? (
                    <div className="tree-empty">
                        <svg className="tree-empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <path d="M9 9h6M9 13h6M9 17h4" />
                        </svg>
                        <p>No organizations found.{canAddRootOrganization&& <><br />Create your first root organization to get started.</>}</p>
                        {canAddRootOrganization&& (
                            <button className="tree-empty-btn" onClick={() => { setSelected(null); setShowCreate(true); }}>
                                <IconPlus /> Create Root Organization
                            </button>
                        )}
                    </div>
                ) : (
                    <ul className="tree-root-list">
                        {treeData.map(node => (
                            <TreeNode
                                key={node._id}
                                node={node}
                                onAddChild={onAddChild}
                                onEdit={onEdit}
                                onSplit={onSplit}
                                selectedBranchId={selectedBranch?._id}
                                onBranchClick={onBranchClick}
                                isAdmin={isAdmin}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {/* ── Zoom Modal ── */}
            {showZoomModal && (
                <div className="tree-zoom-modal-overlay" onClick={() => setShowZoomModal(false)}>
                    <div className="tree-zoom-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="zoom-modal-header">
                            <h3>Organization Tree — {periodLabel}</h3>
                            <div className="zoom-controls">
                                <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out (-)">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="8" y1="11" x2="14" y2="11" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </button>
                                <span className="zoom-level-display">{Math.round(zoomLevel * 100)}%</span>
                                <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In (+)">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="11" cy="11" r="8" />
                                        <line x1="11" y1="8" x2="11" y2="14" />
                                        <line x1="8" y1="11" x2="14" y2="11" />
                                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                </button>
                                <button className="zoom-btn reset" onClick={handleResetZoom} title="Reset View">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                        <path d="M21 3v5h-5" />
                                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                        <path d="M3 21v-5h5" />
                                    </svg>
                                </button>
                                <button className="zoom-close-btn" onClick={() => setShowZoomModal(false)} title="Close (Esc)">
                                    <IconClose />
                                </button>
                            </div>
                        </div>
                        <div 
                            className="zoom-modal-content"
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onTouchStart={handleTouchStart}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        >
                            <div 
                                className="zoom-tree-wrapper"
                                style={{
                                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                                    transformOrigin: 'center center'
                                }}
                            >
                                <ul className="tree-root-list">
                                    {treeData.map(node => (
                                        <TreeNode
                                            key={node._id}
                                            node={node}
                                            onAddChild={onAddChild}
                                            onEdit={onEdit}
                                            onSplit={onSplit}
                                            selectedBranchId={selectedBranch?._id}
                                            onBranchClick={onBranchClick}
                                            isAdmin={isAdmin}
                                        />
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="zoom-modal-hint">
                            💡 Drag to pan • Scroll/Pinch to zoom • +/- keys to zoom • R to reset • ESC to close
                        </div>
                    </div>
                </div>
            )}

            {/* ── Branch Report Drawer ── */}
            {selectedBranch && (
                <BranchReportDrawer
                    branch={selectedBranch}
                    period={period}
                    year={year}
                    month={month}
                    quarter={quarter}
                    onClose={() => setSelectedBranch(null)}
                />
            )}

            {/* ── Modals ── */}
            <CreateOrganizationModal
                show={showCreate}
                parentNode={selected}
                onClose={() => setShowCreate(false)}
                onSuccess={fetchTree}
            />
            <EditOrganizationModal
                show={showEdit}
                organization={selected}
                onClose={() => setShowEdit(false)}
                onSuccess={fetchTree}
            />
            <TargetImportModal
                show={showImport}
                defaultYear={year}
                defaultMonth={period === 'monthly' ? month : undefined}
                onClose={() => setShowImport(false)}
                onSuccess={() => { fetchTree(); fetchDashStats(); }}
            />
            <SplitTargetModal
                show={showSplit}
                organization={selected}
                onClose={() => setShowSplit(false)}
                onSuccess={fetchTree}
            />
        </div>
    );
});

export default OrganizationTree;
